import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getApiV1CommunityList } from '@/api/sdk.gen'
import type { CommunityPost, GetApiV1CommunityListData } from '@/api/types.gen'
import type { LotteryType } from '@/stores/region-store'

export type ForumLotteryFilter = 'all' | LotteryType

type ListQuery = NonNullable<GetApiV1CommunityListData['query']>

// 板块：0 全部主题 / 1 参赛贴 / 2 图解小组
export type ForumBoard = 0 | 1 | 2

// 排序：0 综合 / 1 精华 / 2 点赞 / 3 最新（4 规则走独立路由）
export type ForumSort = 0 | 1 | 2 | 3

const SORT_MAP: Record<ForumSort, NonNullable<ListQuery['sort']>> = {
  0: 'general',
  1: 'quality',
  2: 'likes',
  3: 'newest',
}

// UI 层统一模型，由 API CommunityPost 映射而来
export interface ForumPost {
  postId: number
  userId: string
  username: string
  headPic: string
  createTime: string
  vip: number
  concerned: boolean
  periodStr: string
  top: number
  highQuality: number
  title: string
  description: string
  pictureList?: string[]
  read: boolean
  likeCount: number
  clickCount: number
  commentCount: number
  lotteryType: LotteryType
  isContest: boolean
  raw: CommunityPost
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

function formatPostTime(p: CommunityPost): string {
  const sec = p.publish_time ?? 0
  let d: Date | null = null
  if (sec > 0) d = new Date(sec * 1000)
  else if (p.created_at) d = new Date(p.created_at)
  if (!d || Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

export function mapApiPost(p: CommunityPost): ForumPost {
  return {
    postId: p.id ?? 0,
    userId: String(p.author?.id ?? p.user_id ?? ''),
    username: p.author?.nickname ?? '',
    headPic: p.author?.avatar ?? '',
    createTime: formatPostTime(p),
    vip: p.vip ?? 0,
    concerned: false,
    periodStr: p.period ?? '',
    top: p.pinned ?? 0,
    highQuality: p.high_quality ?? 0,
    title: p.title ?? '',
    description: p.content ?? '',
    pictureList: (p.picture_list || []).length > 0 ? p.picture_list?.map((obj) => obj.imageUrl!) : undefined,
    read: p.is_read ?? false,
    likeCount: p.thumb_up_count ?? p.likes ?? 0,
    clickCount: p.click_count ?? 0,
    commentCount: p.comments ?? p.source_comment_count ?? 0,
    lotteryType: p.lottery_type === 2 ? 'macau' : 'hk',
    isContest: (p.category ?? 0) === 1,
    raw: p,
  }
}

const PAGE_SIZE = 20

export const useForumStore = defineStore('forum-store', () => {
  const board = ref<ForumBoard>(0)
  const sort = ref<ForumSort>(0)
  const lotteryFilter = ref<ForumLotteryFilter>('all')
  const list = ref<ForumPost[]>([])
  const total = ref(0)
  const page = ref(1)
  const loading = ref(false)
  const finished = ref(false)

  function buildQuery(): ListQuery {
    const q: ListQuery = { page: page.value, size: PAGE_SIZE }
    if (lotteryFilter.value !== 'all') {
      q.lottery_type = lotteryFilter.value === 'hk' ? 1 : 2
    }
    q.category = board.value
    q.sort = SORT_MAP[sort.value]
    return q
  }

  async function fetchList(reset = false) {
    if (loading.value) return
    if (reset) {
      page.value = 1
      list.value = []
      finished.value = false
    }
    loading.value = true
    try {
      const res = await getApiV1CommunityList({ query: buildQuery() })
      const data = res.data?.data
      const items = (data?.list ?? []).map(mapApiPost)
      if (reset) list.value = items
      else list.value.push(...items)
      total.value = data?.total ?? list.value.length
      if (items.length < PAGE_SIZE || list.value.length >= total.value) {
        finished.value = true
      } else {
        page.value += 1
      }
    } finally {
      loading.value = false
    }
  }

  function setBoard(b: ForumBoard) {
    if (board.value === b) return
    board.value = b
    fetchList(true)
  }

  function setSort(s: ForumSort) {
    if (sort.value === s) return
    sort.value = s
    fetchList(true)
  }

  function setLotteryFilter(f: ForumLotteryFilter) {
    if (lotteryFilter.value === f) return
    lotteryFilter.value = f
    fetchList(true)
  }

  function markRead(postId: number) {
    const target = list.value.find((p) => p.postId === postId)
    if (target) target.read = true
  }

  return {
    board,
    sort,
    lotteryFilter,
    list,
    loading,
    finished,
    total,
    fetchList,
    setBoard,
    setSort,
    setLotteryFilter,
    markRead,
  }
})
